import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { cn } from '../../lib/cn';

/**
 * Thin wrapper around ECharts that enforces the accessibility shape the
 * spec calls out: every chart carries an `aria-label` summarising the
 * data in prose, plus a visually-hidden `<table>` equivalent for screen
 * readers — exactly what WCAG 2.2 AA + JIS X 8341 require for charts.
 *
 * The visible chart uses the PlyEZ accent palette (sumi-ink derivations),
 * not ECharts' default red/blue/green. Dark mode is picked up via the
 * `theme` prop.
 */

export interface ChartSeries {
  name: string;
  /** For waterfall, `value` is the per-step delta; the running base is derived. */
  data: Array<{ label: string; value: number }>;
}

export interface AccessibleChartProps {
  title: string;
  /** Prose summary read out by screen readers and used as aria-label. */
  summary: string;
  series: ChartSeries[];
  type: 'bar' | 'line' | 'pie' | 'waterfall';
  /** Optional formatter for the numeric axis tooltips. */
  valueFormatter?: (n: number) => string;
  className?: string;
  height?: number;
}

const PALETTE = ['#1F3A6B', '#3A5A9E', '#7791C2', '#B45309', '#6B7280', '#0F172A'];

const WATERFALL_UP = '#B45309'; // amber accent — increases
const WATERFALL_DOWN = '#1F3A6B'; // ink accent — decreases

export interface WaterfallStep {
  label: string;
  delta: number;
  /** Cumulative total after this step. */
  runningTotal: number;
  /** Transparent base bar height so the visible delta bar floats correctly. */
  base: number;
  /** Magnitude of the visible bar (|delta|). */
  magnitude: number;
  isIncrease: boolean;
}

/**
 * Pure running-total computation for the waterfall. Given a list of deltas and
 * a starting total, derive — for each step — the running total, the transparent
 * base offset, and the visible magnitude. The base is the LOWER of the pre- and
 * post-step running totals, so the visible (magnitude) bar always stacks on top
 * of a non-negative transparent base regardless of increase/decrease direction.
 */
export function computeWaterfall(
  deltas: Array<{ label: string; value: number }>,
  start = 0,
): WaterfallStep[] {
  let running = start;
  return deltas.map((d) => {
    const prev = running;
    running = prev + d.value;
    const base = Math.min(prev, running);
    return {
      label: d.label,
      delta: d.value,
      runningTotal: running,
      base,
      magnitude: Math.abs(d.value),
      isIncrease: d.value >= 0,
    };
  });
}

export function AccessibleChart({
  title, summary, series, type, valueFormatter, className, height = 280,
}: AccessibleChartProps) {
  const xLabels = (series[0]?.data ?? []).map((d) => d.label);
  const waterfall = type === 'waterfall' ? computeWaterfall(series[0]?.data ?? []) : null;
  const fmt = valueFormatter ?? ((n: number) => String(n));
  const isCartesian = type === 'bar' || type === 'line' || type === 'waterfall';

  const option: EChartsOption = {
    color: PALETTE,
    tooltip: type === 'waterfall'
      ? {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          formatter: (params: unknown) => {
            const arr = params as Array<{ dataIndex: number }>;
            const step = waterfall?.[arr[0]?.dataIndex ?? 0];
            if (!step) return '';
            return `${step.label}<br/>${fmt(step.delta)} → ${fmt(step.runningTotal)}`;
          },
        }
      : {
          trigger: type === 'pie' ? 'item' : 'axis',
          valueFormatter: valueFormatter ? ((v) => valueFormatter(Number(v))) : undefined,
        },
    grid: type === 'pie' ? undefined : { left: 32, right: 16, top: 24, bottom: 28, containLabel: true },
    legend: series.length > 1 && type !== 'waterfall' ? { top: 0, textStyle: { fontSize: 11 } } : undefined,
    xAxis: !isCartesian ? undefined : {
      type: 'category',
      data: type === 'waterfall' ? (waterfall ?? []).map((s) => s.label) : xLabels,
      axisLine: { lineStyle: { color: '#9CA3AF' } },
      axisLabel: { fontSize: 11, color: '#6B7280' },
    },
    yAxis: !isCartesian ? undefined : {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#E5E7EB' } },
      axisLabel: { fontSize: 11, color: '#6B7280', formatter: valueFormatter ?? undefined },
    },
    series: type === 'waterfall'
      ? [
          {
            // Transparent base — lifts the visible delta bar to its floating position.
            name: '__base',
            type: 'bar',
            stack: 'wf',
            itemStyle: { color: 'transparent' },
            emphasis: { itemStyle: { color: 'transparent' } },
            tooltip: { show: false },
            data: (waterfall ?? []).map((s) => s.base),
          },
          {
            name: title,
            type: 'bar',
            stack: 'wf',
            itemStyle: { borderRadius: [3, 3, 0, 0] },
            data: (waterfall ?? []).map((s) => ({
              value: s.magnitude,
              itemStyle: { color: s.isIncrease ? WATERFALL_UP : WATERFALL_DOWN },
            })),
          },
        ]
      : type === 'pie'
      ? [{
          type: 'pie',
          radius: ['45%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: '#FBF9F5', borderWidth: 2 },
          label: { fontSize: 11 },
          data: series[0]?.data.map((d) => ({ name: d.label, value: d.value })) ?? [],
        }]
      : series.map((s) => ({
          name: s.name,
          type,
          data: s.data.map((d) => d.value),
          smooth: type === 'line',
          itemStyle: { borderRadius: type === 'bar' ? [3, 3, 0, 0] : 0 },
        })),
  };

  return (
    <div className={cn('relative', className)} role="img" aria-label={`${title}. ${summary}`}>
      <ReactECharts option={option} style={{ height, width: '100%' }} notMerge lazyUpdate />
      {/* Screen-reader-only table equivalent — JIS X 8341 §6.2.1 / WCAG SC 1.1.1 */}
      {type === 'waterfall' ? (
        <table className="sr-only">
          <caption>{title}</caption>
          <thead>
            <tr><th>Label</th><th>Change</th><th>Running total</th></tr>
          </thead>
          <tbody>
            {(waterfall ?? []).map((s) => (
              <tr key={s.label}>
                <th>{s.label}</th>
                <td>{fmt(s.delta)}</td>
                <td>{fmt(s.runningTotal)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="sr-only">
          <caption>{title}</caption>
          <thead>
            <tr><th>{type === 'pie' ? 'Slice' : 'Category'}</th>{series.map((s) => <th key={s.name}>{s.name}</th>)}</tr>
          </thead>
          <tbody>
            {(series[0]?.data ?? []).map((d, i) => (
              <tr key={d.label}>
                <th>{d.label}</th>
                {series.map((s) => (
                  <td key={s.name}>{valueFormatter ? valueFormatter(s.data[i]?.value ?? 0) : (s.data[i]?.value ?? 0)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
